import { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import { EntitiesAccessRightsUpdateScope } from "api/generated/model";
import { useRemoveControlEntityRightsForUserMutation } from "org/user/queries/useRemoveControlEntityRightsForUserMutation";

import {
    planRemoval,
    RemovableScope,
    removalLabels,
    RemovalPlan,
} from "./removal";
import { ControlEntityRow } from "./useUserControlEntitiesListTable";

interface UseRemoveControlEntityRightsProps {
    userId: number;
    selectedRows: Row<ControlEntityRow>[];
    deselectAll: () => void;
}

export function useRemoveControlEntityRights({
    userId,
    selectedRows,
    deselectAll,
}: UseRemoveControlEntityRightsProps) {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    // The modal works on the selection it was opened with, so it stays stable while the list
    // refetches and while it animates out after the selection is cleared.
    const [plan, setPlan] = useState<RemovalPlan>(() =>
        planRemoval([], EntitiesAccessRightsUpdateScope.control)
    );
    const [understood, setUnderstood] = useState(false);
    const { mutate, isPending } = useRemoveControlEntityRightsForUserMutation();

    const selected = selectedRows.map((row) => row.original);

    const toolState = (scope: RemovableScope): TextToolState =>
        planRemoval(selected, scope).entityIds.length === 0
            ? { type: "DISABLED" }
            : { type: "ENABLED" };

    const open = (scope: RemovableScope) => {
        setPlan(planRemoval(selected, scope));
        setUnderstood(false);
        setIsModalOpen(true);
    };

    const remove = () => {
        const { entityIds, scope } = plan;
        mutate(
            { userId, data: { entityIds, scope } },
            {
                onSuccess: ({ data: { changed } }) => {
                    emitNotification(
                        "success",
                        removalLabels(t, scope).removed(changed)
                    );
                    deselectAll();
                    setIsModalOpen(false);
                },
            }
        );
    };

    return {
        toolState,
        open,
        close: () => setIsModalOpen(false),
        remove,
        isRemoving: isPending,
        isModalOpen,
        plan,
        understood,
        setUnderstood,
    };
}
